/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/FiledToLane.tsx
 * FILE_ID: EMAILHUB-READERHEADER-LANES-FILEDTO-001
 * PURPOSE: Where this email lives — primary placement, +N, org home, visibility.
 * ROLE IN ARCHITECTURE: Bounded lane leaf of the expanded triage context.
 * MAIN EXPORTS: FiledToLane.
 * NON-RESPONSIBILITIES: Placement CRUD (PlacementManager owns it); review
 *   resolution (ReviewQueue owns it).
 * NOTES FOR FUTURE AI: No permanent "+ board / lane" chip at rest (D269-06)
 *   — adding placements happens inside the Manager. Suggested org/job chips
 *   are suppressed while the decision line owns the confirm/reject pair
 *   (D269-12, suppressDecisionAffordances).
 *   Action-only controls (Move org, Refer) and secondary status chrome
 *   (AI tag, Private/visibility) live behind LaneActionsPlus, not in the
 *   resting chip run (change #302 / #427) — the lane states the placement
 *   fact at rest and offers the rest on demand.
 */
import { Briefcase } from 'lucide-react'
import type { getSamKanbanApi } from '@quotech/kanban-host'

import { CardAiTag } from '../../components/CardAiTag'
import { CardVisibilityBadge } from '../../components/CardVisibilityBadge'
import { EmailVisibilityToggle } from '../../components/EmailVisibilityToggle'
import { HomeOrgBadge } from '../../components/HomeOrgBadge'
import { RehomeOrgControl } from '../../components/RehomeOrgControl'
import { SuggestedJobChip } from '../../components/SuggestedJobChip'
import { resolveCardAiState } from '../../lib/cardAiState'
import { ReferToOrgAction, SuggestedOrgChip } from '../../referrals'
import { useEmailActions, type Email } from '../../hooks/useEmails'
import { ReaderPlacementsControl } from '../placements/PlacementManager'
import { placementNeedsReview, selectReaderPlacements } from '../model/placements'
import { cn } from '../../../_internal/lib/utils'
import { LANE_CONTROL_CLASS, LANE_CONTROL_ICON } from './laneControl'
import { LaneActionsPlus } from './LaneActionsPlus'
import { LaneShell, type LaneShellProps } from './LaneShell'

export interface FiledToLaneProps
  extends Pick<LaneShellProps, 'surfaceProps' | 'onOpenEllipsis' | 'expand'> {
  email: Email
  api: ReturnType<typeof getSamKanbanApi>
  orgId: number | null
  onRefetch?: () => void
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  onOpenCard?: (cardId: string) => void
  onOpenBoard?: () => void
  onPeekJob: (jobId: number) => void
  hideJobLink?: boolean
  suppressDecisionAffordances?: boolean
  /** Controlled Placement Manager state — the lane menu opens it. */
  placementManagerOpen: boolean
  onPlacementManagerOpenChange: (open: boolean) => void
  /** Scroll/focus the Review Queue in the triage band. */
  onOpenReviewQueue?: () => void
}

/**
 * Renders the Filed To lane.
 *
 * Inputs: email, kanban api, org context, host callbacks, controlled
 * manager state.
 * Output: home-org when distinct, job chip, primary placement + "+N",
 * review-count link; AI/visibility/move/refer behind the lane "+" .
 * Failure mode: no placements → the Manager's dashed "Add placement"
 * affordance (rendered by ReaderPlacementsControl, not a permanent chip).
 */
export function FiledToLane({
  email,
  surfaceProps,
  onOpenEllipsis,
  api,
  orgId,
  onRefetch,
  onPatchEmail,
  onOpenCard,
  onOpenBoard,
  onPeekJob,
  hideJobLink = false,
  suppressDecisionAffordances = false,
  placementManagerOpen,
  onPlacementManagerOpenChange,
  onOpenReviewQueue,
  expand,
}: FiledToLaneProps) {
  const cardAi = resolveCardAiState(email)
  const allocateOrgId = email.organisation ?? orgId
  const reviewCount = selectReaderPlacements(email).filter(placementNeedsReview).length
  const { setVisibility } = useEmailActions()

  const patchSuggestion = (patch: Partial<Email>) => {
    if (onPatchEmail) {
      onPatchEmail(email.id, patch)
      return
    }
    onRefetch?.()
  }

  // Change #1210. The server answers 403 for anyone but the mailbox owner;
  // the toggle then stays on the stored value and the hook shows the toast.
  const changeVisibility = async (next: 'public' | 'private') => {
    const updated = await setVisibility(email.id, next)
    patchSuggestion({ visibility: updated?.visibility ?? next })
  }

  return (
    <LaneShell
      lane="filedTo"
      surfaceProps={surfaceProps}
      onOpenEllipsis={onOpenEllipsis}
      expand={expand}
      trailing={
        reviewCount > 0 && onOpenReviewQueue ? (
          <button
            type="button"
            data-testid="filedto-review-link"
            onClick={onOpenReviewQueue}
            className={cn(
              LANE_CONTROL_CLASS,
              'bg-amber-100 font-medium text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200',
            )}
          >
            {reviewCount} to review
          </button>
        ) : null
      }
    >
      {/* Board · lane first — FILED answers "where is this filed?" Org home
          is secondary context and used to crowd out the placement chip
          (operator 2026-08-05: orphan "Priority" + "Newmark" with no board). */}
      <ReaderPlacementsControl
        email={email}
        api={api}
        orgId={allocateOrgId}
        onPatchEmail={onPatchEmail}
        onChanged={onRefetch}
        onOpenCard={onOpenCard}
        onOpenBoard={() => onOpenBoard?.()}
        suppressReviewControls={suppressDecisionAffordances}
        manageOpen={placementManagerOpen}
        onManageOpenChange={onPlacementManagerOpenChange}
      />
      <HomeOrgBadge
        organisationId={email.organisation}
        organisationName={email.organisation_name}
        shellOrgId={orgId}
      />
      {/* Public | Private sits in the resting run on purpose (operator
          2026-10-29): the choice must be one click, not behind the "+". */}
      <EmailVisibilityToggle value={email.visibility} onChange={changeVisibility} />
      {!suppressDecisionAffordances && (
        <SuggestedOrgChip
          emailId={email.id}
          suggested={email.suggested_org}
          onChanged={() => patchSuggestion({ suggested_org: null })}
        />
      )}
      {/* Mutations + secondary chrome (AI / Private) rest behind one "+" so
          the board placement keeps the lane width (change #427). */}
      <LaneActionsPlus label="Filed to actions" data-testid="filedto-actions-plus">
        <CardAiTag state={cardAi.ai} />
        <CardVisibilityBadge visibility={cardAi.visibility} />
        <RehomeOrgControl emailId={email.id} onRehomed={onRefetch} />
        <ReferToOrgAction emailId={email.id} onChanged={onRefetch} />
      </LaneActionsPlus>
      {!hideJobLink && email.job_id ? (
        <button
          type="button"
          onClick={() => onPeekJob(email.job_id!)}
          title={`Peek at ${email.job_name || `Job #${email.job_id}`} - "Open job" inside completes the navigation`}
          data-testid="job-peek-chip"
          className={cn(
            LANE_CONTROL_CLASS,
            'max-w-[200px] bg-blue-100 text-blue-700 hover:underline dark:bg-blue-900/30 dark:text-blue-300',
          )}
        >
          <Briefcase className={LANE_CONTROL_ICON} />
          <span className="min-w-0 truncate">{email.job_name || `Job #${email.job_id}`}</span>
        </button>
      ) : !hideJobLink && email.suggested_job && !suppressDecisionAffordances ? (
        <SuggestedJobChip
          emailId={email.id}
          suggested={email.suggested_job}
          onConfirmed={() =>
            patchSuggestion({
              job_id: email.suggested_job!.job_id,
              job_name: email.suggested_job!.job_label,
              suggested_job: null,
            })
          }
          onDismissed={() => patchSuggestion({ suggested_job: null })}
        />
      ) : null}
    </LaneShell>
  )
}
